<template>
  <div class="wd-text-black50">
    <member-content-title :title="$t('miles_page_title')" />

    <div class="wd-border-0 wd-border-b wd-border-solid wd-border-gray40 wd--mx-5 wd-mb-5 wd-px-5 wd-pb-6 sm:wd-mx-0 sm:wd-mb-4 sm:wd-px-0 sm:wd-pb-10">
      <div class="wd-flex wd-justify-center wd-items-center wd-gap-2 wd-mt-4">
        <span class="wd-text-otempmax wd-font-medium">{{ formattedMileAmount }}</span>
        <funnow-icon active-class="wd-h-10 wd-w-10" raw image-path="/images/membership/funmiles.svg" />
      </div>
      <p v-if="mileExpiredDateText" class="wd-text-omd wd-mt-4 !wd-mb-0 wd-text-center">
        {{ mileExpiredDateText }}
      </p>
    </div>

    <template v-for="section in contentSections" :key="section.type">
      <p class="wd-text-otemplg wd-font-medium wd-py-4 !wd-mb-0">{{ section.sectionTitle }}</p>

      <div class="sm:wd-flex sm:wd--mx-5">
        <div v-for="block in section.blocks" :key="block.type" class="wd-flex wd-gap-4 wd-py-5 sm:wd-basis-1/2 sm:wd-px-10">
          <div class="wd-flex-shrink-0 wd-p-1">
            <funnow-icon :image-path="block.imagePath" width="64" height="64" raw />
          </div>
          <div class="wd-flex-1">
            <h3 class="wd-text-omd wd-font-medium wd-mb-2">{{ block.title }}</h3>
            <p class="wd-text-otempsm wd-whitespace-pre-line !wd-mb-0">
              {{ block.content }}
            </p>
          </div>
        </div>
      </div>
    </template>

    <a v-if="faqLink" :href="faqLink" target="_blank" class="wd-flex wd-items-center wd-justify-between wd--mr-3 wd-py-2">
      <span class="wd-text-omd wd-text-black50">{{ $t('all_faq') }}</span>
      <funnow-icon image-path="chevron-right.svg" active-class="wd-text-gray60 wd-h-10 wd-w-10 wd-p-2" />
    </a>
  </div>
</template>

<script setup lang="ts">
import { useIntlNumber } from '@/composition/useIntl'
import { useFunMiles } from '@/composition/useMember'
import { useMemberPageAuth } from '@/composition/useMemberPageAuth'

import MemberContentTitle from '@/components/member/MemberContentTitle.vue'
import FunnowIcon from '@/components/icons/funnow-icon.vue'

import { FAQ_LINK, FAQ_SECTION_IDS } from '@/constants/links'
import type { FmemberMemberInfo } from '@/types/api'

definePageMeta({
  layout: 'member',
})

const { $store: store, $dayjs } = useNuxtApp()
const { getLocaleNumber } = useIntlNumber()
const { funMilesImages } = useFunMiles()
const { locale, t } = useI18n()

useMemberPageAuth()

const user = computed<FmemberMemberInfo | null>(() => store.getters['authentication/getUser'])
const faqLink = computed(() => {
  const baseUrl = FAQ_LINK[locale.value]

  return baseUrl ? `${baseUrl}/sections/${FAQ_SECTION_IDS.funMiles}` : ''
})
const contentSections = computed(() => [
  {
    type: 'accumulate',
    sectionTitle: t('miles_page_how_to_gain_title'),
    blocks: [
      {
        type: 'spend',
        imagePath: funMilesImages.value[0],
        title: t('miles_gain_by_order_title'),
        content: t('miles_gain_by_order_content'),
      },
      {
        type: 'upgrade',
        imagePath: funMilesImages.value[1],
        title: t('miles_gain_by_bonus_title'),
        content: t('miles_gain_by_bonus_content'),
      },
    ],
  },
  {
    type: 'use',
    sectionTitle: t('miles_page_how_to_use_title'),
    blocks: [
      {
        type: 'benefit',
        imagePath: funMilesImages.value[2],
        title: t('miles_use_to_upgrade_title'),
        content: t('miles_use_to_upgrade_content'),
      },
    ],
  },
])
const funMilesAmount = computed(() => user.value?.benefit?.mile || 0)
const formattedMileAmount = computed(() => getLocaleNumber(funMilesAmount.value))
const mileExpiredDateText = computed(() => {
  return user.value?.benefit?.mile_expired_at && funMilesAmount.value > 0
    ? t('miles_expire_date', { expire_date: $dayjs(user.value?.benefit?.mile_expired_at).format('YYYY/MM/DD') })
    : ''
})
</script>
